import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    SortOrder,
    listFiltersService,
    ListTableSort,
    mapToDropdownOption,
    depascalize,
    getEnumValues,
    DropdownOption
} from "sprint-common-web";

import { DistributorsDataContext } from "../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { OrderRequestStatus } from "../../../../common/types/OrderRequestStatus";
import { OrderAirlinesDataContext } from "../../../common/data-providers/OrderAirlinesDataProvider";
import { OrderCompletionProgress } from "../../../common/types/OrderCompletionProgress";
import { getCompletionProgressOptions } from "../../../common/utils/getCompletionProgressOptions";
import { getUniqueSystemDropdownOptions } from "../../../orders/common/getUniqueSystemDropdownOptions";
import { OrderRequestListingContext } from "../context/OrderRequestListingContext";
import { OrderRequestSource } from "../OrderRequestListingItem";

export interface OrderRequestListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    pONumber?: string;
    content?: string;
    filename?: string;
    dueDateStart?: Date;
    dueDateEnd?: Date;
    airlineIds?: string[];
    systemIds?: string[];
    statuses?: number[];
    progresses?: number[];
    sources?: number[];
    distributorId?: string;
    distributorName?: string;
    assetsAssociationStates?: number[];
}

const statuses = [
    OrderRequestStatus.Pending,
    OrderRequestStatus.Invalid,
    OrderRequestStatus.CloudProcessing,
    OrderRequestStatus.OnPremProcessing,
    OrderRequestStatus.Cancelled
];

const sources = getEnumValues(OrderRequestSource);

enum AssetsAssociationState {
    FullAssociation = 1,
    PartialAssociation = 2,
    NoAssociation = 3
}

const defaultFilters: OrderRequestListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "createdDate",
    sortOrder: "desc",
    statuses: [OrderRequestStatus.Pending, OrderRequestStatus.Invalid],
    dueDateStart: undefined,
    dueDateEnd: undefined,
    airlineIds: undefined,
    distributorId: undefined,
    distributorName: undefined,
    systemIds: undefined,
    progresses: getEnumValues(OrderCompletionProgress),
    sources,
    content: "",
    pONumber: "",
    filename: "",
    assetsAssociationStates: getEnumValues(AssetsAssociationState)
};

export const useOrderRequestListingFilters = () => {
    const { airlines } = useContext(OrderAirlinesDataContext);
    const { listingConfig } = useContext(OrderRequestListingContext);

    const history = useHistory();
    const location = useLocation();

    const { suggestDistributor } = useContext(DistributorsDataContext);

    const filtersService = useMemo(() => listFiltersService<OrderRequestListingFilters>(defaultFilters), []);

    const [filters, setFilters] = useState<OrderRequestListingFilters>(() => {
        const fetchedFilters = filtersService.getSavedFilters(location.search);

        return { ...fetchedFilters, pageSize: listingConfig.pageSize ?? fetchedFilters.pageSize };
    });

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setStatuses = (orderStatuses: number[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, statuses: orderStatuses, page: 1 }));
    };

    const setSources = (orderRequestSources: number[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            sources: orderRequestSources,
            page: 1
        }));
    };

    const setAssetsAssociationStates = (assetsAssociationStates: number[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            assetsAssociationStates,
            page: 1
        }));
    };

    const setDistributor = (distributorId?: string, distributorName?: string): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            distributorId,
            distributorName,
            page: 1
        }));
    };

    const setAirlines = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            airlineIds: options.map((option) => String(option.key)),
            systemIds: undefined,
            page: 1
        }));
    };

    const setFilename = (filename: string): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, filename, page: 1 }));
    };

    const setSystems = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({
            ...prevFilters,
            systemIds: options.map((option) => option.key),
            page: 1
        }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setDueDateStart = (dueDateStart?: Date): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, dueDateStart, page: 1 }));
    };

    const setDueDateEnd = (dueDateEnd?: Date): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, dueDateEnd, page: 1 }));
    };

    const setPONumber = (pONumber?: string): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, pONumber, page: 1 }));
    };

    const setProgresses = (progresses?: number[]): void => {
        setFilters((prevFilters: OrderRequestListingFilters) => ({ ...prevFilters, progresses, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const airlineOptions = airlines.map((airline) => mapToDropdownOption(airline));

    const statusOptions = statuses.map((status) => ({ key: status, text: depascalize(OrderRequestStatus[status]) }));

    const sourceOptions = sources.map((source) => ({ key: source, text: depascalize(OrderRequestSource[source]) }));

    const progressOptions = getCompletionProgressOptions();

    const systemOptions = getUniqueSystemDropdownOptions(airlines, filters.airlineIds);

    const assetsAssociationStatesOptions = getEnumValues(AssetsAssociationState).map((state) => ({
        key: state,
        text: depascalize(AssetsAssociationState[state])
    }));

    const filterDistributorOptions = async (query: string) => {
        const distributors = await suggestDistributor(query);

        return distributors.map((distributor) => mapToDropdownOption(distributor));
    };

    return {
        filters,
        airlineOptions,
        systemOptions,
        sourceOptions,
        progressOptions,
        assetsAssociationStatesOptions,
        filterDistributorOptions,
        setPage,
        setPageSize,
        setSort,
        setStatuses,
        setSources,
        setAssetsAssociationStates,
        setDistributor,
        setAirlines,
        setSystems,
        setContent,
        setPONumber,
        setProgresses,
        setFilename,
        statusOptions,
        setDueDateStart,
        setDueDateEnd,
        clearFilters
    };
};
